import { useCallback, useEffect, useRef } from 'react';
import { LAYOUT_TYPE } from '@/app/components/search/components/LayoutManager/utils.ts';
import { DESIGN_SYSTEM_TRIGGER } from '@/app/common/components/Pill';
import { returnFocusWhenAvailable } from '@/app/common/utils/focus.ts';

// The mobile list's floating "Map" pill is a design-system Pill, whose focusable node is its inner trigger.
const VIEW_MODE_TOGGLE_SELECTOR = `[data-view-mode-toggle], .pill-view-mode ${DESIGN_SYSTEM_TRIGGER}`;

const getViewModeToggle = () =>
  Array.from(document.querySelectorAll<HTMLElement>(VIEW_MODE_TOGGLE_SELECTOR)).find(
    (toggle) => toggle.getClientRects().length > 0
  );
const getViewOnMapButton = (hotelId: number) => document.querySelector<HTMLElement>(`[data-view-on-map="${hotelId}"]`);

const isMapLayout = (layoutType: LAYOUT_TYPE) =>
  layoutType === LAYOUT_TYPE.DESKTOP_MAP || layoutType === LAYOUT_TYPE.MOBILE_MAP;

/*
 * MPH-261: list and map are different layout roots, so switching between them unmounts the control
 * the user just activated and focus falls to <body>. After a switch the user asked for, focus lands
 * on the view-mode toggle of the new layout; back on the list, it returns to the card's "View on map"
 * button when that is what opened the map. Layout changes the user did not ask for (a resize across
 * the breakpoint) are left alone.
 */
export const useViewModeSwitchFocus = (layoutType: LAYOUT_TYPE) => {
  const switchRequestedRef = useRef(false);
  const cardOpenerRef = useRef<number | undefined>(undefined);

  const handleViewModeSwitchRequested = useCallback((fromCardHotelId?: number) => {
    switchRequestedRef.current = true;
    if (fromCardHotelId) cardOpenerRef.current = fromCardHotelId;
  }, []);

  useEffect(() => {
    const requested = switchRequestedRef.current;
    switchRequestedRef.current = false;
    if (isMapLayout(layoutType)) return requested ? returnFocusWhenAvailable(getViewModeToggle) : undefined;

    // Any return to the list ends the map visit, so the card opener is dropped even when focus is left alone.
    const cardHotelId = cardOpenerRef.current;
    cardOpenerRef.current = undefined;
    if (!requested) return undefined;
    return returnFocusWhenAvailable(() => (cardHotelId && getViewOnMapButton(cardHotelId)) || getViewModeToggle());
  }, [layoutType]);

  return { handleViewModeSwitchRequested };
};
